import SFetch from '@app/utils/sfetch'

/**
 * Fetches period picker data for a company
 * @param companyId - The ID of the company
 * @param ledgerId - The current ledger ID to use as reference
 * @returns The period picker data including years, ledgersPerYear, and currentLedgerId
 */
export async function fetchPeriodPickerData(
  companyId: number,
  ledgerId: string
): Promise<{
  years: number[]
  ledgersPerYear: Record<number, unknown[]>
  currentLedgerId: string
}> {
  // Create the URL using the available Routes
  const url = (window.Silverfin.Routes as Record<string, unknown>)
    .ledger_period_picker_path as (params: {
    firm_id: string
    company_id: number
    ledger_id: string
    _options: Record<string, unknown>
  }) => string

  const urlString = url({
    firm_id: window.Silverfin.currentFirmId,
    company_id: companyId,
    ledger_id: ledgerId,
    _options: {},
  })

  return (await SFetch.json(urlString, {
    method: 'GET',
  })) as {
    years: number[]
    ledgersPerYear: Record<number, unknown[]>
    currentLedgerId: string
  }
}

/**
 * Updates a company consolidation with new start and end dates and parent company status
 * @param consolidationId - The ID of the consolidation to update
 * @param startDate - The new start date or null to remove it
 * @param endDate - The new end date or null to remove it
 * @param setParentCompany - Whether this company is a parent company (optional, only send when changed)
 * @returns The fetch promise
 */
export async function updateConsolidation(
  consolidationId: number,
  {
    startDate,
    endDate,
    setParentCompany,
  }: {
    startDate: string | null
    endDate: string | null
    setParentCompany?: boolean
  }
): Promise<unknown> {
  const url = window.Silverfin.Routes.company_consolidation_path(
    window.Silverfin.currentFirmId,
    window.Silverfin.currentCompanyId,
    consolidationId
  )

  const requestData: {
    company_consolidation: {
      start_date: string | null
      end_date: string | null
      set_parent_company?: boolean
    }
  } = {
    company_consolidation: {
      start_date: startDate,
      end_date: endDate,
    },
  }

  // Only include set_parent_company if it was provided
  if (setParentCompany !== undefined) {
    requestData.company_consolidation.set_parent_company = setParentCompany
  }

  return await SFetch.json(url, {
    method: 'PUT',
    body: requestData,
  })
}
